import { HttpErrorResponse } from '@angular/common/http';
import { Observable, from, of } from 'rxjs';
import { concatMap, catchError, scan, last } from 'rxjs/operators';
import { ListItem } from '../../models/ListItem';
import { AcsScopedLocs } from '../models/acs.models';
import { ACS_GROUP_PREFIX } from './acs.constants';

export function resolveLocCode(locCode: string, locList: ListItem[]): string {
  if (locCode.startsWith(ACS_GROUP_PREFIX)) {
    const loc: ListItem | undefined = locList.find(l => l.value === locCode);
    if (loc) {
      return loc.text.split(' - ')[0];
    }
  }
  return locCode;
}

export function toTitleCase(str: string): string {
  if (!str) return str;
  return str.replace(/\w\S*/g, (txt) =>
    txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase()
  );
}

export function scopedLocsToListItems(result: AcsScopedLocs, includeGroups: boolean): ListItem[] {
  const items: ListItem[] = [];
  if (includeGroups) {
    for (const g of result.groups) {
      items.push({ id: g.groupId, value: ACS_GROUP_PREFIX + g.groupId, text: g.groupCode + ' - ' + g.groupName });
    }
  }
  for (const l of result.locs) {
    items.push({ id: 0, value: l.locCode, text: l.locCode + ' - ' + (l.description || '') });
  }
  return items;
}

export function extractErrorMessage(err: HttpErrorResponse, fallback: string): string {
  if (err.error?.detail) {
    return err.error.detail;
  }
  if (err.error?.title) {
    return err.error.title;
  }
  return fallback;
}

export interface BulkDeleteResult {
  total: number;
  succeeded: number;
  failed: number;
  errors: string[];
}

export function executeBulkDelete<T>(
  ids: T[],
  deleteFn: (id: T) => Observable<any>
): Observable<BulkDeleteResult> {
  const initial: BulkDeleteResult = { total: ids.length, succeeded: 0, failed: 0, errors: [] };
  return from(ids).pipe(
    concatMap((id: T) =>
      deleteFn(id).pipe(
        concatMap(() => of({ success: true, error: '' })),
        catchError((err: HttpErrorResponse) => of({ success: false, error: extractErrorMessage(err, 'Delete failed') }))
      )
    ),
    scan((acc: BulkDeleteResult, result: { success: boolean; error: string }) => {
      if (result.success) {
        return { ...acc, succeeded: acc.succeeded + 1 };
      }
      return { ...acc, failed: acc.failed + 1, errors: [...acc.errors, result.error] };
    }, initial),
    last()
  );
}

export function formatBulkDeleteToast(result: BulkDeleteResult, itemName: string): { message: string; type: 'success' | 'error' } {
  if (result.failed === 0) {
    return { message: `All ${result.succeeded} ${itemName}(s) deleted`, type: 'success' };
  }
  if (result.succeeded === 0) {
    return { message: `Failed to delete ${result.failed} ${itemName}(s)`, type: 'error' };
  }
  return { message: `Deleted ${result.succeeded} of ${result.total} ${itemName}(s). ${result.failed} failed.`, type: 'error' };
}
